import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Check, 
  X, 
  Play, 
  Pause, 
  Mic, 
  Camera,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  orderBy, 
  onSnapshot,
  updateDoc,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function ContributionsScreen() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [story, setStory] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending', 'approved', 'rejected', 'all'
  
  // Audio playback
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  
  // Action state
  const [processingId, setProcessingId] = useState(null);
  
  // Load story and contributions
  useEffect(() => {
    const loadStory = async () => {
      const storyDoc = await getDoc(doc(db, 'lifeStories', storyId));
      if (storyDoc.exists()) {
        setStory({ id: storyDoc.id, ...storyDoc.data() });
      }
    };
    
    loadStory();
    
    // Subscribe to contributions
    const contributionsRef = collection(db, 'lifeStories', storyId, 'contributions');
    const q = query(contributionsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setContributions(items);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [storyId]);
  
  const handleApprove = async (contributionId) => {
    setProcessingId(contributionId);
    try {
      const functions = getFunctions();
      const reviewContribution = httpsCallable(functions, 'reviewContribution');
      await reviewContribution({
        storyId,
        contributionId,
        action: 'approve'
      });
    } catch (err) {
      console.error('Error approving:', err);
      alert('Failed to approve. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };
  
  const handleReject = async (contributionId) => {
    if (!window.confirm('Are you sure you want to reject this contribution?')) {
      return;
    }
    
    setProcessingId(contributionId);
    try {
      const functions = getFunctions();
      const reviewContribution = httpsCallable(functions, 'reviewContribution');
      await reviewContribution({
        storyId,
        contributionId,
        action: 'reject'
      });
    } catch (err) {
      console.error('Error rejecting:', err);
      alert('Failed to reject. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };
  
  const togglePlayback = (contribution) => {
    if (playingId === contribution.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = contribution.audioUrl;
        audioRef.current.play();
        setPlayingId(contribution.id);
      }
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const filteredContributions = contributions.filter(c => {
    if (filter === 'all') return true;
    return c.status === filter;
  });
  
  const pendingCount = contributions.filter(c => c.status === 'pending').length;
  const approvedCount = contributions.filter(c => c.status === 'approved').length;
  const rejectedCount = contributions.filter(c => c.status === 'rejected').length;
  
  const residentName = story?.preferredName || story?.residentName || 'Resident';
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-quiltt-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setPlayingId(null)}
        className="hidden" 
      />
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="font-semibold text-gray-900">Family Contributions</h1>
            <p className="text-sm text-gray-500">{residentName}'s Life Story</p>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setFilter('pending')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'pending'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Clock className="w-4 h-4" />
            Pending
            {pendingCount > 0 && (
              <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {pendingCount}
              </span>
            )}
          </button>
          
          <button
            onClick={() => setFilter('approved')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'approved'
                ? 'bg-green-100 text-green-700'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            Approved
            {approvedCount > 0 && (
              <span className="text-xs text-gray-500">({approvedCount})</span>
            )}
          </button>
          
          <button
            onClick={() => setFilter('rejected')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'rejected'
                ? 'bg-red-100 text-red-700'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <XCircle className="w-4 h-4" />
            Rejected
            {rejectedCount > 0 && (
              <span className="text-xs text-gray-500">({rejectedCount})</span>
            )}
          </button>
          
          <button
            onClick={() => setFilter('all')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              filter === 'all'
                ? 'bg-gray-50 text-quiltt-navy'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            All
            <span className="text-xs text-gray-500">({contributions.length})</span>
          </button>
        </div>
        
        {/* Contributions list */}
        {filteredContributions.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {filter === 'pending' ? (
                <Clock className="w-8 h-8 text-gray-400" />
              ) : (
                <CheckCircle className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {filter === 'pending' ? 'No pending contributions' : `No ${filter} contributions`}
            </h3>
            <p className="text-gray-500">
              {filter === 'pending' 
                ? 'When family members contribute, their submissions will appear here for review.'
                : 'Contributions will appear here once reviewed.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredContributions.map((contribution) => (
              <div
                key={contribution.id}
                className={`bg-white rounded-xl border p-5 ${
                  contribution.status === 'pending' 
                    ? 'border-amber-200' 
                    : contribution.status === 'approved'
                    ? 'border-green-200'
                    : 'border-red-200'
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      contribution.type === 'memory' ? 'bg-gray-50' : 'bg-purple-100'
                    }`}>
                      {contribution.type === 'memory' ? (
                        <Mic className={`w-5 h-5 ${contribution.type === 'memory' ? 'text-quiltt-navy' : 'text-purple-600'}`} />
                      ) : (
                        <Camera className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {contribution.contributorName}
                        {contribution.relationship && (
                          <span className="text-gray-500 font-normal"> ({contribution.relationship})</span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {contribution.type === 'memory' ? 'Shared a memory' : 'Added a photo'}
                        {contribution.duration && ` • ${formatDuration(contribution.duration)}`}
                      </p>
                    </div>
                  </div>
                  
                  {/* Status badge */}
                  <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    contribution.status === 'pending'
                      ? 'bg-amber-100 text-amber-700'
                      : contribution.status === 'approved'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {contribution.status.charAt(0).toUpperCase() + contribution.status.slice(1)}
                  </div>
                </div>
                
                {/* Content */}
                {contribution.type === 'memory' && (
                  <div className="mb-4">
                    {/* Prompt if present */}
                    {contribution.prompt && (
                      <p className="text-sm text-gray-500 mb-2 italic">
                        Topic: "{contribution.prompt}"
                      </p>
                    )}
                    
                    {/* Audio player */}
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <button
                        onClick={() => togglePlayback(contribution)}
                        className="w-10 h-10 rounded-full bg-quiltt-purple hover:bg-purple-700 flex items-center justify-center flex-shrink-0"
                      >
                        {playingId === contribution.id ? (
                          <Pause className="w-5 h-5 text-white" />
                        ) : (
                          <Play className="w-5 h-5 text-white ml-0.5" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        {contribution.transcript ? (
                          <p className="text-sm text-gray-700 line-clamp-3">
                            {contribution.transcript}
                          </p>
                        ) : contribution.transcriptStatus === 'pending' ? (
                          <p className="text-sm text-gray-500 italic">Transcribing...</p>
                        ) : (
                          <p className="text-sm text-gray-500 italic">No transcript available</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {contribution.type === 'photo' && (
                  <div className="mb-4">
                    <img
                      src={contribution.photoUrl}
                      alt="Contributed photo"
                      className="w-full max-w-md rounded-lg"
                    />
                    {contribution.caption && (
                      <p className="mt-2 text-sm text-gray-600">{contribution.caption}</p>
                    )}
                  </div>
                )}
                
                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-xs text-gray-400">
                    {formatDate(contribution.createdAt)}
                  </p>
                  
                  {/* Actions for pending */}
                  {contribution.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleReject(contribution.id)}
                        disabled={processingId === contribution.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                        Reject
                      </button>
                      <button
                        onClick={() => handleApprove(contribution.id)}
                        disabled={processingId === contribution.id}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                      >
                        {processingId === contribution.id ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Approve
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
